import { ArrowUp, ArrowUpRight, Instagram, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/LanguageContext";
import { CALENDLY_URL, EMAIL, INSTAGRAM_URL } from "@/i18n";
import { scrollToId } from "@/utils";
import { ChapterLabel, MaskedLine, Reveal, Segments } from "@/components/Reveal";

const Footer = () => {
  const { t } = useLanguage();
  const f = t.footer;

  return (
    <footer id="contatti" className="relative overflow-hidden bg-noom-ink text-white" data-testid="footer-section">
      <div className="mx-auto max-w-[1600px] px-6 pb-10 pt-24 lg:px-16 lg:pt-36">
        <ChapterLabel dark testId="footer-label">
          {f.label}
        </ChapterLabel>
        <h2 className="mt-8 font-display text-[clamp(2.4rem,6.5vw,5.5rem)] font-semibold leading-[1.0] tracking-[-0.03em]">
          {f.lines.map((line, i) => (
            <MaskedLine key={i} inView delay={i * 0.12}>
              <Segments segments={line} accentClass="text-noom-lilac" />
            </MaskedLine>
          ))}
        </h2>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-[#A1A1AA] sm:text-lg">{f.sub}</p>
        </Reveal>
        <Reveal delay={0.42} className="mt-10">
          <a
            data-testid="footer-book-call-button"
            href={CALENDLY_URL}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 rounded-full bg-noom-lilac px-8 py-4 text-sm font-semibold text-noom-ink transition-colors duration-300 hover:bg-white"
          >
            {f.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>

        <div className="mt-20 grid gap-10 border-t border-[#262626] pt-16 lg:grid-cols-12 lg:gap-14" data-testid="footer-bio">
          <Reveal className="lg:col-span-3">
            <img
              src="/assets/giorgia-portrait.jpg"
              alt="Giorgia Sgarbi nel suo studio"
              className="aspect-[4/5] w-full max-w-[240px] border border-[#262626] object-cover"
              data-testid="footer-bio-photo"
            />
          </Reveal>
          <div className="lg:col-span-9">
            <Reveal delay={0.1}>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-noom-lilac">{f.bio.label}</p>
              <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
                {f.bio.greeting}
              </h3>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-5 max-w-2xl space-y-4 text-sm leading-relaxed text-[#A1A1AA] sm:text-base">
                {f.bio.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-[#8E8E93]">{f.bio.role}</p>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 grid gap-10 border-t border-[#262626] pt-12 sm:grid-cols-3">
          <Reveal>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#8E8E93]">
              <Mail className="h-3.5 w-3.5 text-noom-lilac" />
              {f.emailLabel}
            </p>
            <a
              data-testid="footer-email-link"
              href={`mailto:${EMAIL}`}
              className="mt-4 block font-display text-xl font-medium tracking-tight transition-colors duration-300 hover:text-noom-lilac sm:text-2xl"
            >
              {EMAIL}
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#8E8E93]">
              <Instagram className="h-3.5 w-3.5 text-noom-lilac" />
              {f.igLabel}
            </p>
            <a
              data-testid="footer-instagram-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-4 block font-display text-xl font-medium tracking-tight transition-colors duration-300 hover:text-noom-lilac sm:text-2xl"
            >
              @studionoom
            </a>
          </Reveal>
          <Reveal delay={0.2} className="sm:justify-self-end">
            <button
              data-testid="footer-back-top"
              onClick={() => scrollToId("#top")}
              className="group inline-flex items-center gap-3 rounded-full border border-[#262626] px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-noom-lilac hover:text-noom-lilac"
            >
              {f.back}
              <ArrowUp className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>

        <div className="mt-16" data-testid="footer-instagram-feed">
          <Reveal>
            <div className="flex items-baseline justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8E8E93]">{f.igFeed}</p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-noom-lilac transition-colors duration-300 hover:text-white"
              >
                @studionoom
              </a>
            </div>
          </Reveal>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {["/assets/cesare-cover.jpg", "/assets/nero-cover.jpg", "/assets/collage-1.jpg", "/assets/logofolio-dimora.jpg"].map(
              (src, i) => (
                <Reveal key={src} delay={i * 0.06}>
                  <a
                    data-testid={`footer-ig-post-${i + 1}`}
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative block aspect-square overflow-hidden border border-[#262626]"
                  >
                    <img
                      src={src}
                      alt="studio noom su Instagram"
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-noom group-hover:scale-105"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-noom-ink/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <Instagram className="h-5 w-5 text-noom-lilac" />
                    </span>
                  </a>
                </Reveal>
              )
            )}
          </div>
        </div>

        <div className="pointer-events-none mt-20 select-none overflow-hidden" aria-hidden="true">
          <img src="/assets/logo-light.png" alt="" className="mx-auto w-full max-w-5xl opacity-[0.08]" />
        </div>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-[#262626] py-6 sm:flex-row sm:items-center">
          <p className="font-mono text-[11px] tracking-[0.08em] text-[#8E8E93]" data-testid="footer-legal">
            © 2026 Studio Noom. Tutti i diritti riservati. — P.IVA 04242440362
          </p>
          <div className="flex items-center gap-6">
            <Link
              data-testid="footer-privacy-link"
              to="/privacy"
              className="font-mono text-[11px] uppercase tracking-[0.14em] text-[#8E8E93] transition-colors duration-300 hover:text-noom-lilac"
            >
              Privacy Policy
            </Link>
            <p className="font-mono text-[11px] tracking-[0.08em] text-[#8E8E93]">{f.tagline}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
